import { Alert, Col, Form, Row, Input, Checkbox, Button, Icon } from 'antd';
import './CreditCardForm.less';
import React, { useRef, useState } from 'react';
import {
    CardCvvElement,
    CardMonthElement,
    CardNumberElement,
    CardNumberElementChangeEvent,
    CardYearElement,
    IndividualElementChangeEvent,
    useRecurly,
} from '@recurly/react-recurly';
import { SSC_TOS_URL } from '../../settings/config';
import { Token } from '../types';

const availableCards = [
    {
        imageSrc: '/images/visa_light.png',
        name: 'Visa',
    },
    {
        imageSrc: '/images/mastercard_light.png',
        name: 'MasterCard',
    },
    {
        imageSrc: '/images/american_express_light.png',
        name: 'American Express',
    },
    {
        imageSrc: '/images/jcb_light.png',
        name: 'JCB',
    },
    {
        imageSrc: '/images/discover_light.png',
        name: 'Discover',
    },
    {
        imageSrc: '/images/diners_club_light.png',
        name: 'Diners Club',
    },
];

type Props = {
    buttonLabel: string;
    errorMessage?: string;
    isLoading?: boolean;
    showAvailableCards?: boolean;
    onToken: (token: Token) => void;
    onError: (message: string) => void;
};

export default function CreditCardForm({ buttonLabel, errorMessage, isLoading, showAvailableCards, onToken, onError }: Props) {
    const formRef = useRef(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});
    const [cardNumberValidation, setCardNumberValidation] = useState<CardNumberElementChangeEvent | undefined>();
    const [cardMonthValidation, setCardMonthValidation] = useState<IndividualElementChangeEvent | undefined>();
    const [cardYearValidation, setCardYearValidation] = useState<IndividualElementChangeEvent | undefined>();
    const [cvcValidation, setCvcValidation] = useState<IndividualElementChangeEvent | undefined>();

    const [acceptTos, setAcceptTos] = useState(false);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [zipCode, setZipCode] = useState('');

    const recurly = useRecurly();

    const handleFocus = (event?: React.FocusEvent<HTMLInputElement, Element>) => {
        if (event?.type === 'month' || event?.type === 'year') {
            setValidationErrors((st) => ({ ...st, expirationDate: false }));
        }
        if (event?.type === 'number') {
            setValidationErrors((st) => ({ ...st, number: false }));
        }
        if (event?.type === 'cvv') {
            setValidationErrors((st) => ({ ...st, cvc: false }));
        }
    };

    const handleNameFocus = (event: React.FocusEvent<HTMLInputElement, Element>) => {
        const name = event.target.name;
        if (name === 'first_name') {
            setValidationErrors((st) => ({ ...st, firstName: false }));
        }

        if (name === 'last_name') {
            setValidationErrors((st) => ({ ...st, lastName: false }));
        }
    };

    const handleZipCodeFocus = () => {
        setValidationErrors((st) => ({ ...st, zipCode: false }));
    };

    const handleAcceptTosChange = () => {
        setAcceptTos(!acceptTos);
        setValidationErrors((st) => ({ ...st, acceptTos: false }));
    };

    const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();

        if (!formRef.current) {
            onError('Something went wrong. Please try again');
            return;
        }

        let hasErrors = false;

        if (!cardNumberValidation?.valid) {
            hasErrors = true;
            setValidationErrors((st) => ({ ...st, number: true }));
        }
        if (!cardMonthValidation?.valid || !cardYearValidation?.valid) {
            hasErrors = true;
            setValidationErrors((st) => ({ ...st, expirationDate: true }));
        }
        if (!cvcValidation?.valid) {
            hasErrors = true;
            setValidationErrors((st) => ({ ...st, cvc: true }));
        }
        if (firstName.length === 0) {
            hasErrors = true;
            setValidationErrors((st) => ({ ...st, firstName: true }));
        }
        if (lastName.length === 0) {
            hasErrors = true;
            setValidationErrors((st) => ({ ...st, lastName: true }));
        }
        if (zipCode.length === 0) {
            hasErrors = true;
            setValidationErrors((st) => ({ ...st, zipCode: true }));
        }
        if (!acceptTos) {
            hasErrors = true;
            setValidationErrors((st) => ({ ...st, acceptTos: true }));
        }

        if (hasErrors) {
            return;
        }

        recurly.token(formRef.current, (err, token) => {
            if (err) {
                let formSubmitError = 'Credit card validation failed!';
                formSubmitError = err.message
                    ? `${formSubmitError} ${err.message}`
                    : ' Please check your information and try again or contact our support team!';

                onError(formSubmitError);
            } else {
                onToken(token);
            }
        });
    };

    return (
        <>
            {errorMessage && (
                <div className="card-validation">
                    <Alert
                        type="error"
                        showIcon
                        message={errorMessage}
                    />
                </div>
            )}

            <form
                className="credit-card-form"
                onSubmit={handleSubmit}
                ref={formRef}
            >
                {showAvailableCards && (
                    <Form.Item
                        className="accepted-cards"
                        label="Accepted cards"
                    >
                        {availableCards.map((card) => (
                            <img
                                key={card.name}
                                src={card.imageSrc}
                                alt={card.name}
                                title={card.name}
                            />
                        ))}
                    </Form.Item>
                )}

                <Form.Item
                    className="card-number"
                    validateStatus={validationErrors.number ? 'error' : 'validating'}
                    help={validationErrors.number ? 'Invalid card number' : ''}
                >
                    <CardNumberElement
                        className="card-number-input"
                        onFocus={handleFocus}
                        onChange={setCardNumberValidation}
                        onSubmit={handleSubmit}
                        displayIcon={true}
                        style={{
                            // @ts-ignore
                            placeholder: { content: 'Card number', color: 'rgba(66,71,71,.64)', fontWeight: '300' },
                        }}
                    />
                </Form.Item>

                <Form.Item>
                    <Row gutter={16}>
                        <Col span={12}>
                            <div className="card-expiration-date">
                                <Form.Item
                                    validateStatus={validationErrors['expirationDate'] ? 'error' : 'validating'}
                                    help={validationErrors['expirationDate'] ? 'Invalid expiration date' : ''}
                                >
                                    <Input.Group compact>
                                        <Row>
                                            <Col span={12}>
                                                <CardMonthElement
                                                    className="card-month-input"
                                                    onFocus={handleFocus}
                                                    onSubmit={handleSubmit}
                                                    onChange={setCardMonthValidation}
                                                    inputType="select"
                                                    style={{
                                                        // @ts-ignore
                                                        placeholder: { content: 'MM', color: 'rgba(66,71,71,.64)', fontWeight: '300' },
                                                    }}
                                                />
                                            </Col>
                                            <Col span={12}>
                                                <CardYearElement
                                                    className="card-year-input"
                                                    onFocus={handleFocus}
                                                    onSubmit={handleSubmit}
                                                    onChange={setCardYearValidation}
                                                    inputType="select"
                                                    style={{
                                                        // @ts-ignore
                                                        placeholder: { content: 'YY', color: 'rgba(66,71,71,.64)', fontWeight: '300' },
                                                    }}
                                                />
                                            </Col>
                                        </Row>
                                    </Input.Group>
                                </Form.Item>
                            </div>
                        </Col>
                        <Col span={12}>
                            <div className="card-verification-code">
                                <Form.Item
                                    validateStatus={validationErrors['cvc'] ? 'error' : 'validating'}
                                    help={validationErrors['cvc'] ? 'Invalid CVV' : ''}
                                >
                                    <CardCvvElement
                                        className="card-cvv-input"
                                        onFocus={handleFocus}
                                        onSubmit={handleSubmit}
                                        onChange={setCvcValidation}
                                        style={{
                                            // @ts-ignore
                                            placeholder: { content: 'CVV', color: 'rgba(66,71,71,.64)', fontWeight: '300' },
                                        }}
                                    />
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>
                </Form.Item>

                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item
                            className="first-name-input"
                            validateStatus={validationErrors['firstName'] ? 'error' : 'validating'}
                            help={validationErrors['firstName'] ? 'Invalid name' : ''}
                        >
                            <Input
                                name="first_name"
                                data-recurly="first_name"
                                placeholder="First name"
                                spellCheck="false"
                                onChange={(event) => setFirstName(event.target.value)}
                                onFocus={handleNameFocus}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            className="last-name-input"
                            validateStatus={validationErrors['lastName'] ? 'error' : 'validating'}
                            help={validationErrors['lastName'] ? 'Invalid name' : ''}
                        >
                            <Input
                                name="last_name"
                                data-recurly="last_name"
                                placeholder="Last name"
                                spellCheck="false"
                                onChange={(event) => setLastName(event.target.value)}
                                onFocus={handleNameFocus}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            className="zip-code-input"
                            validateStatus={validationErrors['zipCode'] ? 'error' : 'validating'}
                            help={validationErrors['zipCode'] ? 'Invalid ZIP code' : ''}
                        >
                            <Input
                                name="postal_code"
                                data-recurly="postal_code"
                                placeholder="ZIP code"
                                spellCheck="false"
                                onChange={(event) => setZipCode(event.target.value)}
                                onFocus={handleZipCodeFocus}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Row>
                    <Form.Item
                        className="tos-form-item"
                        validateStatus={validationErrors['acceptTos'] ? 'error' : 'validating'}
                        help={validationErrors['acceptTos'] ? 'You must agree with our terms of service!' : ''}
                    >
                        <Checkbox
                            className="accept-tos-checkbox"
                            onChange={handleAcceptTosChange}
                        >
                            I accept the&nbsp;
                            <a
                                href={SSC_TOS_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Terms of Service
                            </a>
                        </Checkbox>
                    </Form.Item>

                    <Form.Item className="place-order-form-item">
                        <Button
                            className={`place-order ${!acceptTos ? 'disabled' : ''}`}
                            type="primary"
                            htmlType="submit"
                            block
                            loading={isLoading}
                        >
                            {buttonLabel}
                            <Icon
                                type="lock"
                                theme="filled"
                            />
                        </Button>
                    </Form.Item>
                </Row>
            </form>
        </>
    );
}
